// store/syncModule.js
import { syncService } from '@/services/syncService';
import dataService from '@/services/dataService';
import axios from '@/api/axios';
import logService from '@/services/logService';

export const syncModule = {
    namespaced: true,

    state: {
        isOnline: navigator.onLine,
        syncInProgress: false,
        lastSyncAttempt: null,
        lastSuccessfulSync: null,
        pendingUpdates: [],
        autoSyncEnabled: localStorage.getItem('autoSyncEnabled') !== 'false',
        syncStats: {
            totalProcessed: 0,
            successCount: 0,
            errorCount: 0,
            lastErrorMessage: null,
            errorDetails: {}
        },
        refreshIntervalMinutes: 5, // Intervalle de rafraîchissement par défaut
        refreshInProgress: false,
        lastRefreshTime: null
    },

    mutations: {
        SET_ONLINE_STATUS(state, status) {
            state.isOnline = status;
        },
        SET_SYNC_STATUS(state, status) {
            state.syncInProgress = status;
        },
        SET_LAST_SYNC_ATTEMPT(state, timestamp) {
            state.lastSyncAttempt = timestamp;
        },
        SET_LAST_SUCCESSFUL_SYNC(state, timestamp) {
            state.lastSuccessfulSync = timestamp;
        },
        ADD_PENDING_UPDATE(state, update) {
            state.pendingUpdates.push(update);
        },
        REMOVE_PENDING_UPDATE(state, updateId) {
            state.pendingUpdates = state.pendingUpdates.filter(update => update.id !== updateId);
        },
        SET_PENDING_UPDATES(state, updates) {
            state.pendingUpdates = updates;
        },
        SET_AUTO_SYNC(state, enabled) {
            state.autoSyncEnabled = enabled;
            localStorage.setItem('autoSyncEnabled', enabled.toString());
        },
        UPDATE_SYNC_STATS(state, { processed, success, error, errorMessage, errorDetails }) {
            if (processed) state.syncStats.totalProcessed += processed;
            if (success) state.syncStats.successCount += success;
            if (error) state.syncStats.errorCount += error;
            if (errorMessage) state.syncStats.lastErrorMessage = errorMessage;
            if (errorDetails) state.syncStats.errorDetails = { ...state.syncStats.errorDetails, ...errorDetails };
        },
        RESET_SYNC_STATS(state) {
            state.syncStats = {
                totalProcessed: 0,
                successCount: 0,
                errorCount: 0,
                lastErrorMessage: null,
                errorDetails: {}
            };
        },
        UPDATE_DELIVERY_IN_TOURNEE(state, { deliveryId, updates }) {
            // Implementation of the mutation
        },
        SET_REFRESH_STATUS(state, status) {
            state.refreshInProgress = status;
        },
        SET_LAST_REFRESH_TIME(state, timestamp) {
            state.lastRefreshTime = timestamp;
        },
        SET_REFRESH_INTERVAL(state, minutes) {
            state.refreshIntervalMinutes = minutes;
        }
    },

    actions: {
        async initializeNetworkListeners({ commit, dispatch, state, rootState }) {
            // Initialiser le dataService avant tout
            await dataService.init();

            // Effectuer un nettoyage initial du cache d'images et des données synchronisées au démarrage
            try {
                console.log("Lancement du nettoyage initial du cache d'images...");
                const cleanupResults = await syncService.purgeImageStorage(12); // Conserver seulement les images de moins de 12h
                
                // Nettoyer également les mises à jour déjà synchronisées
                await syncService.cleanup();
                
                logService.syncInfo(`Nettoyage initial du cache effectué`, {
                    imagesDeleted: cleanupResults.deleted,
                    imagesKept: cleanupResults.kept,
                    reasons: cleanupResults.reasons,
                    timestamp: new Date().toISOString()
                });
            } catch (cleanupError) {
                console.error("Erreur lors du nettoyage initial du cache:", cleanupError);
                logService.syncWarning(`Échec du nettoyage initial du cache`, {
                    error: cleanupError.message,
                    stack: cleanupError.stack
                });
            }

            // Variable pour suivre si le log de synchronisation a déjà été émis
            let syncLogEmitted = false;
            let autoSyncInterval = null;
            
            // Écouteur pour l'événement de synchronisation terminée
            const onSyncCompleted = async (event) => {
                try {
                    // Vérifier s'il faut rafraîchir les données depuis le serveur
                    if (state.isOnline && !state.userDefinedOfflineMode) {
                        // Utiliser une action existante pour rafraîchir les données de la tournée au lieu de
                        // refreshTourneeData qui n'existe pas dans le store racine
                        if (rootState.tourneeData?.id) {
                            // Si on a déjà des données de tournée, utiliser getTourneeData de la racine
                            await dispatch('getTourneeData', null, { root: true });
                        } else {
                            // Sinon, rafraîchir juste les mises à jour en attente
                            await dispatch('refreshPendingUpdates');
                        }
                    }
                } catch (error) {
                    dispatch('logs/syncError', {
                        message: 'Erreur lors du rafraîchissement des données après synchronisation',
                        data: { error: error.message }
                    }, { root: true });
                }
            };
            
            // Supprimer tout écouteur existant pour éviter les doublons
            window.removeEventListener('sync-completed', onSyncCompleted);
            // Ajouter l'écouteur
            window.addEventListener('sync-completed', onSyncCompleted);

            // Fonction pour démarrer la synchronisation automatique
            const startAutoSync = (intervalMinutes = 5) => {
                if (autoSyncInterval) {
                    clearInterval(autoSyncInterval);
                }
                
                const intervalMs = intervalMinutes * 60 * 1000;
                
                autoSyncInterval = setInterval(async () => {
                    if (navigator.onLine && !state.syncInProgress) {
                        try {
                            await dispatch('synchronize');
                        } catch (error) {
                            dispatch('logs/syncError', {
                                message: 'Erreur lors de la synchronisation automatique',
                                data: { error: error.message }
                            }, { root: true });
                        }
                    }
                }, intervalMs);
                
                dispatch('logs/syncInfo', {
                    message: `Synchronisation automatique activée (intervalle: ${intervalMinutes} minutes)`,
                    data: { timestamp: new Date().toISOString() }
                }, { root: true });
            };

            // Gestionnaire pour le retour en ligne
            window.addEventListener('online', async () => {
                commit('SET_ONLINE_STATUS', true);
                
                // Logger le retour en ligne
                dispatch('logs/syncInfo', {
                    message: 'Connexion internet rétablie',
                    data: { timestamp: new Date().toISOString() }
                }, { root: true });
                
                // Vérifier s'il y a des données à synchroniser
                try {
                    const pendingUpdates = await syncService.exportPendingData();
                    const hasPendingData = pendingUpdates.updates.length > 0 || pendingUpdates.images.length > 0;
                    
                    console.log('Données en attente détectées après retour en ligne:', {
                        updates: pendingUpdates.updates.length,
                        images: pendingUpdates.images.length,
                        shopDeliveryImages: pendingUpdates.images.filter(img => 
                            img.type === 'shop_delivery' || 
                            img.deliveryContext?.includes('shop') || 
                            img.context?.includes('shop')
                        ).length,
                        standardImages: pendingUpdates.images.filter(img => 
                            img.type !== 'shop_delivery' && 
                            !img.deliveryContext?.includes('shop') && 
                            !img.context?.includes('shop')
                        ).length
                    });
                    
                    // Log détaillé des images shop_delivery pour diagnostic
                    pendingUpdates.images.forEach(img => {
                        if (img.type === 'shop_delivery' || 
                            img.deliveryContext?.includes('shop') || 
                            img.context?.includes('shop')) {
                            console.log(`Image shop_delivery en attente: ${img.id}`, {
                                type: img.type,
                                context: img.deliveryContext,
                                synced: img.synced || false
                            });
                        }
                    });
                    
                    if (hasPendingData) {
                        dispatch('logs/syncInfo', {
                            message: 'Données en attente de synchronisation détectées',
                            data: {
                                updates: pendingUpdates.updates.length,
                                images: pendingUpdates.images.length,
                                shopDeliveryUpdates: pendingUpdates.updates.filter(u => u.type === 'shop_delivery').length,
                                standardUpdates: pendingUpdates.updates.filter(u => u.type !== 'shop_delivery').length
                            }
                        }, { root: true });
                        
                        // Synchroniser systématiquement les données en attente lors du retour en ligne,
                        // même si la synchronisation automatique est désactivée
                        dispatch('logs/syncInfo', {
                            message: 'Synchronisation automatique déclenchée suite au retour en ligne',
                            data: {
                                updates: pendingUpdates.updates.length,
                                images: pendingUpdates.images.length,
                                autoSyncEnabled: state.autoSyncEnabled
                            }
                        }, { root: true });
                        
                        // Démarrer la synchronisation avec un délai de 2 secondes pour laisser la connexion se stabiliser
                        setTimeout(async () => {
                            try {
                                // Pour un diagnostic plus précis, utiliser directement la fonction forceSyncAndRefresh
                                // plutôt que de passer par l'action 'synchronize'
                                console.log('Démarrage de forceSyncAndRefresh() après passage en ligne...');
                                const result = await syncService.forceSyncAndRefresh();
                                console.log('Résultat de forceSyncAndRefresh():', result);
                                
                                // Vérifier s'il reste des données non synchronisées
                                const remainingData = await syncService.exportPendingData();
                                if (remainingData.images.some(img => 
                                    (img.type === 'shop_delivery' || 
                                     img.deliveryContext?.includes('shop') || 
                                     img.context?.includes('shop')) && 
                                    !img.synced)) {
                                    console.warn('Images shop_delivery non synchronisées après tentative:', 
                                        remainingData.images.filter(img => 
                                            (img.type === 'shop_delivery' || 
                                             img.deliveryContext?.includes('shop') || 
                                             img.context?.includes('shop')) && 
                                            !img.synced
                                        ).map(img => ({
                                            id: img.id,
                                            type: img.type,
                                            context: img.deliveryContext
                                        }))
                                    );
                                    
                                    // Tentative supplémentaire spécifique pour les images shop_delivery
                                    console.log('Tentative supplémentaire spécifique pour les images shop_delivery...');
                                    await syncService.syncAll();
                                }
                            } catch (syncError) {
                                console.error('Erreur lors de la synchronisation après passage en ligne:', syncError);
                                dispatch('logs/syncError', {
                                    message: 'Erreur lors de la synchronisation après passage en ligne',
                                    data: { error: syncError.message, stack: syncError.stack }
                                }, { root: true });
                            }
                        }, 2000);
                    } else {
                        // S'il n'y a pas de données en attente, rafraîchir les données depuis le serveur
                        setTimeout(() => {
                            if (rootState.tourneeData?.id) {
                                dispatch('getTourneeData', null, { root: true });
                            }
                        }, 2000);
                    }
                } catch (error) {
                    console.error('Erreur lors de la vérification des données en attente:', error);
                    dispatch('logs/syncError', {
                        message: 'Erreur lors de la vérification des données en attente',
                        data: { error: error.message, stack: error.stack }
                    }, { root: true });
                }
                
                // Redémarrer la synchronisation automatique si elle était activée
                if (state.autoSyncEnabled && navigator.onLine) {
                    startAutoSync(5); // Toutes les 5 minutes
                }
            });

            // Gestionnaire pour la perte de connexion
            window.addEventListener('offline', () => {
                commit('SET_ONLINE_STATUS', false);
                dispatch('logs/syncWarning', {
                    message: 'Connexion internet perdue, passage en mode hors ligne',
                    data: { timestamp: new Date().toISOString() }
                }, { root: true });
                
                // Arrêter la synchronisation automatique lorsqu'on est hors ligne
                if (autoSyncInterval) {
                    clearInterval(autoSyncInterval);
                    autoSyncInterval = null;
                }
            });
            
            // Vérifier si la synchronisation automatique est activée
            if (state.autoSyncEnabled && navigator.onLine) {
                startAutoSync(5); // Toutes les 5 minutes
            }

            // Charger les updates en attente dans le state
            dispatch('refreshPendingUpdates');
        },

        toggleAutoSync({ commit, state, dispatch }) {
            const newValue = !state.autoSyncEnabled;
            commit('SET_AUTO_SYNC', newValue);
            
            // Re-initialiser les écouteurs réseau pour démarrer/arrêter la synchronisation automatique
            dispatch('initializeNetworkListeners');
            
            dispatch('logs/syncInfo', {
                message: newValue ? 'Synchronisation automatique activée' : 'Synchronisation automatique désactivée',
                data: { 
                    timestamp: new Date().toISOString(),
                    interval: newValue ? '5 minutes' : null
                }
            }, { root: true });
            
            return newValue;
        },

        async updateDeliveryWithImages({ commit, dispatch, state, rootState }, { deliveryId, status, undoneReason, consignes_rendues_v2, images, waitForSync = false }) {
            try {
                const imageIds = [];
                // Collecter les IDs serveur des images déjà uploadées
                const serverImageIds = [];

                dispatch('logs/syncInfo', {
                    message: `Début de mise à jour de la livraison ${deliveryId}`,
                    data: {
                        status,
                        undoneReason,
                        imageCount: images?.length || 0
                    }
                }, { root: true });

                // Traiter les images d'abord
                if (images?.length) {
                    dispatch('logs/syncDebug', {
                        message: `Traitement de ${images.length} images pour la livraison ${deliveryId}`,
                        data: { status }
                    }, { root: true });
                    
                    for (const image of images) {
                        // Si l'image a déjà un ID serveur, l'ajouter directement au tableau serverImageIds
                        if (image.serverImageId) {
                            serverImageIds.push(image.serverImageId);
                            dispatch('logs/syncDebug', {
                                message: `Image déjà uploadée avec ID serveur ${image.serverImageId}`,
                                data: {
                                    deliveryId,
                                    context: image.context
                                }
                            }, { root: true });
                            continue;
                        }

                        if (!image.file || !(image.file instanceof Blob)) {
                            dispatch('logs/syncWarning', {
                                message: 'Image invalide ignorée',
                                data: {
                                    deliveryId,
                                    context: image.context
                                }
                            }, { root: true });
                            continue;
                        }

                        const imageId = await dataService.storePendingImage(image.file, {
                            deliveryId,
                            deliveryContext: image.context
                        });
                        imageIds.push(imageId);
                        
                        dispatch('logs/syncDebug', {
                            message: `Image stockée localement avec succès`,
                            data: {
                                deliveryId,
                                imageId,
                                context: image.context
                            }
                        }, { root: true });
                    }
                }

                // Créer la mise à jour
                const updateData = {
                    id: `update_${Date.now()}`,
                    deliveryId,
                    status,
                    undoneReason,
                    consignes_rendues_v2,
                    imageIds,
                    serverImageIds, // Ajouter les IDs serveur pour les images déjà uploadées
                    timestamp: new Date().toISOString()
                };

                // Stocker la mise à jour
                await dataService.storePendingUpdate(updateData);
                commit('ADD_PENDING_UPDATE', updateData);
                
                dispatch('logs/syncInfo', {
                    message: `Mise à jour ${updateData.id} stockée localement avec succès`,
                    data: {
                        deliveryId,
                        status,
                        imageCount: imageIds.length,
                        serverImageCount: serverImageIds.length
                    }
                }, { root: true });

                // Mettre à jour les données de la livraison courante si c'est celle-ci
                if (rootState.currentDelivery?.id === deliveryId) {
                    commit('UPDATE_CURRENT_DELIVERY', {
                        status,
                        undone_reason: undoneReason,
                        consignes_rendues_v2
                    }, { root: true });
                    
                    dispatch('logs/syncDebug', {
                        message: 'Livraison courante mise à jour en mémoire',
                        data: { deliveryId, status }
                    }, { root: true });
                }

                // Mettre à jour les données locales de la tournée
                // En utilisant dataService au lieu de la mutation directe
                await dataService.updateDeliveryInTournee(deliveryId, {
                    status,
                    undone_reason: undoneReason,
                    consignes_rendues_v2
                });

                // S'assurer que l'interface est mise à jour immédiatement
                commit('UPDATE_DELIVERY_IN_TOURNEE', {
                    deliveryId,
                    updates: {
                        status,
                        undone_reason: undoneReason,
                        consignes_rendues_v2
                    }
                }, { root: true });
                
                // Émettre un événement globalement pour notifier tous les composants concernés
                syncService.emitDeliveryUpdatedEvent(deliveryId, {
                                status,
                                undone_reason: undoneReason,
                                consignes_rendues_v2
                            });

                // Mettre à jour le cache de la livraison
                const currentDelivery = rootState.currentDelivery || {};
                await dataService.saveDelivery(deliveryId, {
                    ...currentDelivery,
                    status,
                    undone_reason: undoneReason,
                    consignes_rendues_v2
                });

                // Si en ligne, lancer la synchronisation immédiate sans condition
                if (state.isOnline) {
                    dispatch('logs/syncInfo', {
                        message: 'Synchronisation immédiate de la livraison en cours',
                        data: { deliveryId, updateId: updateData.id }
                    }, { root: true });
                    
                    // Option d'attente: si true, permet aux images d'être traitées avant la synchro complète
                    if (waitForSync && images?.length > 0) {
                        // Attendre un court délai pour permettre aux images d'être traitées
                        dispatch('logs/syncInfo', {
                            message: `Attente de 3 secondes avant synchronisation pour permettre le traitement des images`,
                            data: { deliveryId, imageCount: images.length }
                        }, { root: true });
                        
                        await new Promise(resolve => setTimeout(resolve, 3000));
                    }
                    
                    // Effectuer la synchronisation immédiatement, même si autoSyncEnabled est false
                    await dispatch('synchronize');
                } 
                // Si hors ligne et synchro automatique activée, on laisse le système gérer
                else if (!state.isOnline && state.autoSyncEnabled) {
                    dispatch('logs/syncInfo', {
                        message: 'Appareil hors ligne: les données seront synchronisées automatiquement au retour de la connexion',
                        data: { deliveryId, updateId: updateData.id }
                    }, { root: true });
                }

                return { success: true };
            } catch (error) {
                dispatch('logs/syncError', {
                    message: `Erreur lors de la mise à jour de la livraison ${deliveryId}`,
                    data: {
                        error: error.message,
                        status,
                        stack: error.stack
                    }
                }, { root: true });
                
                commit('UPDATE_SYNC_STATS', { error: 1, errorMessage: error.message });
                return { success: false, error };
            }
        },

        async synchronize({ commit, state, dispatch }) {
            if (state.syncInProgress || !state.isOnline) {
                if (state.syncInProgress) {
                    dispatch('logs/syncWarning', {
                        message: 'Synchronisation déjà en cours, nouvelle demande ignorée'
                    }, { root: true });
                } else if (!state.isOnline) {
                    dispatch('logs/syncWarning', {
                        message: 'Synchronisation impossible : appareil hors ligne'
                    }, { root: true });
                }
                return;
            }

            commit('SET_SYNC_STATUS', true);
            commit('SET_LAST_SYNC_ATTEMPT', Date.now());
            commit('RESET_SYNC_STATS');
            
            dispatch('logs/syncInfo', {
                message: 'Début du processus de synchronisation (envoi de toutes les données en attente)',
                data: { timestamp: new Date().toISOString() }
            }, { root: true });

            try {
                // Récupérer les statistiques avant la synchronisation
                const beforeSync = await syncService.exportPendingData();
                const totalItemsBefore = beforeSync.updates.length + beforeSync.images.length;
                
                dispatch('logs/syncInfo', {
                    message: `État initial: ${totalItemsBefore} éléments à synchroniser (${beforeSync.updates.length} mises à jour, ${beforeSync.images.length} images)`,
                    data: {
                        updates: beforeSync.updates.length,
                        images: beforeSync.images.length
                    }
                }, { root: true });
                
                // Exécuter la synchronisation
                const syncResults = await syncService.syncAll();
                
                // Récupérer les statistiques après la synchronisation
                const afterSync = await syncService.exportPendingData();
                const totalItemsAfter = afterSync.updates.length + afterSync.images.length;
                
                // Calculer combien d'éléments ont été synchronisés
                const syncedItems = Math.max(0, totalItemsBefore - totalItemsAfter);
                
                // Classement des erreurs par type
                const errorDetails = {};
                if (afterSync.updates.length > 0) {
                    // Collecter des informations sur les mises à jour qui ont échoué
                    afterSync.updates.forEach(update => {
                        if (update.lastError) {
                            const status = update.lastError.status || 'network';
                            errorDetails[status] = (errorDetails[status] || 0) + 1;
                        }
                    });
                }
                
                // Mettre à jour les statistiques avec des informations détaillées
                commit('UPDATE_SYNC_STATS', { 
                    processed: totalItemsBefore,
                    success: syncedItems,
                    error: totalItemsAfter,
                    errorDetails: errorDetails
                });
                
                // Mettre à jour le timestamp de dernière synchronisation réussie
                if (syncedItems > 0) {
                    const now = Date.now();
                    commit('SET_LAST_SUCCESSFUL_SYNC', now);
                    
                    // Stocker la date dans localStorage pour la page offline
                    localStorage.setItem('lastSuccessfulSync', now.toString());
                    
                    // Informer le service worker de la dernière synchronisation
                    if (window.swUtils && typeof window.swUtils.notifySyncComplete === 'function') {
                        window.swUtils.notifySyncComplete(now);
                    } else if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                        navigator.serviceWorker.controller.postMessage({
                            type: 'SYNC_TIMESTAMP',
                            timestamp: now
                        });
                    }
                    
                    // Emettre un événement pour permettre le rafraîchissement des données
                    if (syncedItems > 0) {
                        // Créer un événement global pour indiquer que les données ont été synchronisées
                        const event = new CustomEvent('sync-completed', {
                            detail: {
                                timestamp: now,
                                stats: {
                                    processed: totalItemsBefore,
                                    success: syncedItems,
                                    error: totalItemsAfter
                                }
                            }
                        });
                        
                        // Déclencher l'événement
                        window.dispatchEvent(event);
                        document.dispatchEvent(event);
                    }
                }
                
                // Rafraîchir la liste des mises à jour en attente dans le store
                await dispatch('refreshPendingUpdates');
                
                // Nettoyer les anciennes données (mais seulement celles qui ont été synchronisées)
                await syncService.cleanup();
                
                // Vider le cache mémoire des objets File après une synchronisation réussie
                // pour éviter les appels redondants lors des opérations suivantes
                if (dataService.clearFileObjectsCache) {
                    dataService.clearFileObjectsCache();
                    dispatch('logs/syncInfo', {
                        message: 'Cache mémoire des objets File vidé après synchronisation',
                        data: { timestamp: new Date().toISOString() }
                    }, { root: true });
                }
                
                // Identifier si des mises à jour nécessitent une intervention manuelle
                const manualSyncRequired = afterSync.updates.some(update => update.requiresManualSync);
                
                if (manualSyncRequired) {
                    dispatch('logs/syncWarning', {
                        message: 'Certaines mises à jour nécessitent une intervention manuelle après échecs répétés',
                        data: {
                            manualUpdates: afterSync.updates.filter(u => u.requiresManualSync).length
                        }
                    }, { root: true });
                }
                
                dispatch('logs/syncInfo', {
                    message: 'Synchronisation terminée avec succès',
                    data: {
                        syncedItems,
                        remainingUpdates: afterSync.updates.length,
                        remainingImages: afterSync.images.length,
                        success: syncResults.success,
                        failed: syncResults.failed,
                        manualSyncRequired,
                        timestamp: new Date().toISOString()
                    }
                }, { root: true });
                
                return {
                    success: true,
                    syncedItems,
                    remainingItems: totalItemsAfter,
                    errorDetails,
                    manualSyncRequired
                };
            } catch (error) {
                // Créer un message d'erreur plus détaillé et convivial
                let errorMessage = 'Erreur de synchronisation';
                let errorCode = 'unknown';
                
                if (error.response) {
                    // Erreur avec une réponse du serveur
                    errorCode = `status_${error.response.status}`;
                    
                    switch (error.response.status) {
                        case 401:
                            // On ne parle plus de session expirée, on indique simplement qu'il y a un problème d'authentification
                            errorMessage = 'Problème d\'authentification. Les données seront synchronisées ultérieurement.';
                            break;
                        case 403:
                            errorMessage = 'Accès refusé. Vous n\'avez pas les droits nécessaires.';
                            break;
                        case 404:
                            errorMessage = 'Ressource introuvable sur le serveur.';
                            break;
                        case 500:
                        case 502:
                        case 503:
                        case 504:
                            errorMessage = 'Erreur serveur. Les données seront synchronisées ultérieurement.';
                            break;
                        default:
                            errorMessage = `Erreur HTTP ${error.response.status}. Réessai automatique planifié.`;
                    }
                } else if (error.request) {
                    // Erreur sans réponse (network error)
                    errorCode = 'network';
                    errorMessage = 'Erreur réseau. Vérifiez votre connexion internet.';
                } else {
                    // Erreur inattendue
                    errorCode = 'unexpected';
                    errorMessage = error.message || 'Erreur inconnue';
                }
                
                dispatch('logs/syncError', {
                    message: 'Échec de la synchronisation',
                    data: {
                        errorMessage,
                        errorCode,
                        error: error.message,
                        status: error.response?.status,
                        stack: error.stack
                    }
                }, { root: true });
                
                commit('UPDATE_SYNC_STATS', { 
                    error: 1, 
                    errorMessage: errorMessage,
                    errorCode: errorCode
                });
                
                return {
                    success: false,
                    error: errorMessage,
                    errorCode: errorCode
                };
            } finally {
                commit('SET_SYNC_STATUS', false);
            }
        },

        async refreshPendingUpdates({ commit }) {
            try {
                const pendingUpdates = await dataService.getPendingUpdates();
                commit('SET_PENDING_UPDATES', pendingUpdates.filter(update => !update.synced));
            } catch (error) {
                console.error('Erreur lors du rafraîchissement des mises à jour en attente:', error);
            }
        },

        async getDelivery({ state }, deliveryId) {
            try {
                return await dataService.getDelivery(deliveryId);
            } catch (error) {
                console.error(`Erreur lors de la récupération de la livraison ${deliveryId}:`, error);
                return null;
            }
        },

        async getShopDelivery({ state }, deliveryId) {
            try {
                // First check if we have a cached shop delivery
                const cachedDelivery = await dataService.getShopDelivery(deliveryId);
                if (cachedDelivery) {
                    return cachedDelivery;
                }
                
                // If we're online, try to fetch from API
                if (state.isOnline) {
                    try {
                        // Utiliser le bon endpoint pour les shop deliveries
                        const response = await axios.get(`/livraison/shopDelivery/${deliveryId}/`);
                        const shopDeliveryData = response.data;
                        
                        // Cache the data for offline use
                        await dataService.saveShopDelivery(deliveryId, shopDeliveryData);
                        
                        return shopDeliveryData;
                    } catch (apiError) {
                        console.warn(`API fetch failed for shop delivery ${deliveryId}:`, apiError);
                        // Continue to try other strategies
                    }
                }
                
                // If we get here, we couldn't find the delivery
                return null;
            } catch (error) {
                console.error(`Erreur lors de la récupération de la livraison shop ${deliveryId}:`, error);
                return null;
            }
        },
        
        async refreshTourneeData({ commit, state, dispatch, rootState }) {
            // Si une synchronisation ou un rafraîchissement est déjà en cours, ne rien faire
            if (state.syncInProgress || state.refreshInProgress) {
                return { success: false, reason: 'operation_in_progress' };
            }
            
            // Si l'appareil est hors ligne, ne pas tenter de rafraîchir
            if (!state.isOnline) {
                return { success: false, reason: 'offline' };
            }
            
            try {
                // Marquer le début du rafraîchissement
                commit('SET_REFRESH_STATUS', true);
                const startTime = Date.now();
                
                // Dispatcher l'action de chargement de la tournée au store principal
                await dispatch('loadTourneeData', null, { root: true });
                
                // Mettre à jour le timestamp du dernier rafraîchissement
                commit('SET_LAST_REFRESH_TIME', Date.now());
                
                // Calculer le temps pris pour le rafraîchissement
                const duration = Date.now() - startTime;
                
                dispatch('logs/syncInfo', {
                    message: 'Données de tournée rafraîchies avec succès',
                    data: {
                        duration: `${duration}ms`,
                        timestamp: new Date().toISOString()
                    }
                }, { root: true });
                
                // Vider le cache mémoire des objets File après le rafraîchissement
                // car les données de tournée ont changé
                if (dataService.clearFileObjectsCache) {
                    dataService.clearFileObjectsCache();
                    dispatch('logs/syncInfo', {
                        message: 'Cache mémoire des objets File vidé après rafraîchissement de la tournée',
                        data: { timestamp: new Date().toISOString() }
                    }, { root: true });
                }
                
                return {
                    success: true,
                    duration
                };
            } catch (error) {
                dispatch('logs/syncError', {
                    message: 'Erreur lors du rafraîchissement des données de tournée',
                    data: {
                        error: error.message,
                        stack: error.stack
                    }
                }, { root: true });
                
                return {
                    success: false,
                    error: error.message
                };
            } finally {
                // Marquer la fin du rafraîchissement
                commit('SET_REFRESH_STATUS', false);
            }
        },
        
        async forceSyncAndRefresh({ dispatch, state }) {
            try {
                // Synchroniser d'abord les données en attente
                const syncResult = await dispatch('synchronize');
                
                // Ensuite, rafraîchir les données depuis le serveur si la synchronisation a réussi
                // et que nous sommes en ligne
                if (syncResult.success && state.isOnline) {
                    const refreshResult = await dispatch('refreshTourneeData', null, { root: true });
                    
                    return {
                        sync: syncResult,
                        refresh: refreshResult
                    };
                }
                
                return {
                    sync: syncResult,
                    refresh: null
                };
            } catch (error) {
                dispatch('logs/syncError', {
                    message: 'Erreur lors de la synchronisation forcée avec rafraîchissement',
                    data: { error: error.message }
                }, { root: true });
                
                return {
                    success: false,
                    error: error.message
                };
            }
        },

        async updateShopDelivery({ commit, dispatch, state, rootState }, { id, updates }) {
            try {
                if (!id) {
                    throw new Error('ID de livraison shop requis');
                }

                // Normaliser le status en minuscules pour éviter les doublons d'appels
                const normalizedUpdates = { ...updates };
                if (normalizedUpdates.status) {
                    normalizedUpdates.status = normalizedUpdates.status.toLowerCase();
                }

                dispatch('logs/syncInfo', {
                    message: `Mise à jour de la livraison shop ${id} en cours`,
                    data: { id, updates: normalizedUpdates }
                }, { root: true });

                // Si en ligne, envoyer directement au serveur
                if (state.isOnline) {
                    // Construire le payload de mise à jour
                    const updatePayload = { ...normalizedUpdates };
                    let imageId = null;

                    // Si une photo a été fournie, l'envoyer d'abord
                    if (normalizedUpdates.photo) {
                        try {
                            console.log('Photo détectée dans updateShopDelivery (mode online):', {
                                photoType: typeof normalizedUpdates.photo,
                                hasFile: !!normalizedUpdates.photo.file
                            });
                            
                            // Enregistrer l'ID de l'image pour l'inclure dans la mise à jour
                            imageId = normalizedUpdates.photo;
                            
                            dispatch('logs/syncInfo', {
                                message: `Image associée à la mise à jour shop ${id}`,
                                data: { imageId: normalizedUpdates.photo }
                            }, { root: true });
                        } catch (imageError) {
                            console.error('Erreur lors du traitement de l\'image (mode online):', imageError);
                            dispatch('logs/syncError', {
                                message: `Erreur lors du traitement de l'image pour la livraison shop ${id}`,
                                data: {
                                    error: imageError.message,
                                    imageId: normalizedUpdates.photo
                                }
                            }, { root: true });
                        }
                    }

                    // Appel API pour mettre à jour la livraison shop
                    const response = await axios.patch(`/livraison/shopDelivery/${id}/`, updatePayload);
                    const shopDeliveryData = response.data;

                    // Mettre à jour le cache
                    await dataService.saveShopDelivery(id, shopDeliveryData);

                    // Mettre à jour les données de la livraison courante si c'est celle-ci
                    if (rootState.currentDelivery?.id === id) {
                        commit('UPDATE_CURRENT_DELIVERY', normalizedUpdates, { root: true });
                    }

                    // Émettre un événement pour notifier la mise à jour
                    syncService.emitDeliveryUpdatedEvent(id, normalizedUpdates.status);

                    dispatch('logs/syncInfo', {
                        message: `Livraison shop ${id} mise à jour avec succès`,
                        data: { status: normalizedUpdates.status }
                    }, { root: true });

                    return { success: true, data: shopDeliveryData };
                } else {
                    // En mode hors ligne, enregistrer dans la file d'attente via syncService
                    console.log('Préparation mise à jour shop_delivery en mode offline:', {
                        deliveryId: id,
                        hasPhoto: !!normalizedUpdates.photo,
                        photoType: normalizedUpdates.photo ? typeof normalizedUpdates.photo : 'none'
                    });
                    
                    // Préparer les updates avec les imageIds si une photo est présente
                    const updatesWithImages = { ...normalizedUpdates };
                    
                    // Si la photo est présente, l'ajouter comme imageId explicitement
                    if (normalizedUpdates.photo) {
                        updatesWithImages.imageIds = [normalizedUpdates.photo];
                        console.log('Ajout explicite de l\'imageId à la mise à jour shop delivery:', {
                            imageId: normalizedUpdates.photo,
                            deliveryId: id,
                            status: normalizedUpdates.status
                        });
                    }
                    
                    const updateId = await syncService.queueUpdate('shop_delivery', id, updatesWithImages);

                    dispatch('logs/syncInfo', {
                        message: `Livraison shop ${id} mise en file d'attente pour synchronisation ultérieure`,
                        data: { updateId, hasPhoto: !!normalizedUpdates.photo, imageId: normalizedUpdates.photo }
                    }, { root: true });

                    // Mettre à jour les données de la livraison courante si c'est celle-ci
                    if (rootState.currentDelivery?.id === id) {
                        commit('UPDATE_CURRENT_DELIVERY', normalizedUpdates, { root: true });
                    }

                    return { success: true, synced: false, updateId };
                }
            } catch (error) {
                console.error(`Erreur lors de la mise à jour de la livraison shop ${id}:`, error);
                dispatch('logs/syncError', {
                    message: `Erreur lors de la mise à jour de la livraison shop ${id}`,
                    data: {
                        error: error.message,
                        status: error.response?.status,
                        stack: error.stack
                    }
                }, { root: true });

                throw error;
            }
        }
    },

    getters: {
        isOnline: state => state.isOnline,
        isSyncing: state => state.syncInProgress,
        isRefreshing: state => state.refreshInProgress,
        hasPendingUpdates: state => state.pendingUpdates.length > 0,
        // Getter avec paramètre pour vérifier les mises à jour en attente par livraison
        hasPendingUpdatesForDelivery: (state) => (deliveryId) => {
            return state.pendingUpdates.some(update => update.deliveryId === deliveryId);
        },
        lastSyncAttempt: state => state.lastSyncAttempt,
        lastSuccessfulSync: state => state.lastSuccessfulSync,
        lastRefreshTime: state => state.lastRefreshTime,
        // Obtenir toutes les mises à jour en attente
        getAllPendingUpdates: state => state.pendingUpdates,
        // Obtenir le nombre total de mises à jour en attente
        pendingUpdatesCount: state => state.pendingUpdates.length,
        // Obtenir les statistiques de synchronisation
        getSyncStats: state => state.syncStats,
        // Obtenir des détails sur les erreurs de synchronisation
        getSyncErrorDetails: state => state.syncStats.errorDetails || {},
        // Vérifier si la synchronisation automatique est activée
        isAutoSyncEnabled: state => state.autoSyncEnabled,
        // Calculer le temps écoulé depuis la dernière synchronisation
        timeSinceLastSync: state => {
            if (!state.lastSyncAttempt) return null;
            return Date.now() - state.lastSyncAttempt;
        },
        // Calculer le temps écoulé depuis le dernier rafraîchissement
        timeSinceLastRefresh: state => {
            if (!state.lastRefreshTime) return null;
            return Date.now() - state.lastRefreshTime;
        },
        // Vérifier si le rafraîchissement automatique est nécessaire
        shouldRefresh: state => {
            if (!state.lastRefreshTime) return true;
            
            const elapsed = Date.now() - state.lastRefreshTime;
            const threshold = state.refreshIntervalMinutes * 60 * 1000;
            
            return elapsed >= threshold;
        },
        // Vérifier s'il y a des mises à jour qui nécessitent une intervention manuelle
        hasManualSyncItems: state => async () => {
            try {
                const pendingData = await syncService.exportPendingData();
                return pendingData.updates.some(update => update.requiresManualSync);
            } catch (error) {
                console.error('Erreur lors de la vérification des mises à jour manuelles:', error);
                return false;
            }
        }
    }
};